// RemoveProjectDialog — "Delete …" on a project asks first (pen "Projects
// and Studio", decision 3: a dialog with "Archive instead"), pure.
//
// Removal is final; what lies on the project's canvas stays in the Library
// (the canvas holds references). An active project offers "Archive
// instead", the reversible way to set it aside.

export { RemoveProjectDialog };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../Button/Button.js";
import { Dialog } from "../Dialog/Dialog.js";
import { Text } from "../Text/Text.js";

type RemoveProjectDialogProps = {
    // The project being asked about; null: closed.
    readonly project: {
        readonly title: string;
        readonly archived: boolean;
    } | null;
    readonly onRemove: () => void;
    readonly onArchive: () => void;
    readonly onCancel: () => void;
};

const RemoveProjectDialog = ({
    project,
    onRemove,
    onArchive,
    onCancel,
}: RemoveProjectDialogProps): ReactNode => {
    const { t } = useLingui();
    return (
        <Dialog
            open={project !== null}
            onOpenChange={(open) => {
                if (!open) onCancel();
            }}
            title={
                project === null
                    ? t`Delete project?`
                    : t`Delete “${project.title}”?`
            }
            footer={
                project === null ? null : (
                    <>
                        <Button variant="ghost" onClick={onCancel}>
                            {t`Cancel`}
                        </Button>
                        {project.archived ? null : (
                            <Button variant="secondary" onClick={onArchive}>
                                {t`Archive instead`}
                            </Button>
                        )}
                        <Button variant="danger" onClick={onRemove}>
                            {t`Delete`}
                        </Button>
                    </>
                )
            }
        >
            <Text as="p" tone="dim">
                {t`The project is deleted for good. What is on its canvas stays in Created materials and Uploaded sources.`}
            </Text>
        </Dialog>
    );
};
